import CurrencyFormatter from '@lmn/brg-ui-currency-formatter';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import type { ConfigValuesApp } from '@/app/common/config/configuration/configurationTypes.ts';
import { type PAGE_TYPE, RESULTS_STEP_PAGE_TYPES } from '@/app/common/pageType.ts';
import { PRICE_DISPLAY_TYPE } from '@/app/components/search/utils/formattingTypes.ts';

// TODO: Currency Digits will need to be set correctly based on the currency. GBP, EUR, etc is 2, but JPY for example is 0.
const DEFAULT_CURRENCY_DIGITS = 2;

type Price = number | string;

// this needs to be lowercase otherwise it will break ADS (It also uses '_' instead of '-')
export const createLocaleForAnalytics = ({ locale }: { locale: string }): string =>
  locale.replace('-', '_').toLowerCase();

export const createPriceByDisplayType = (price: number, priceDisplayType: PRICE_DISPLAY_TYPE) =>
  priceDisplayType === PRICE_DISPLAY_TYPE.NO_DECIMALS_FLOOR
    ? Math.floor(price)
    : priceDisplayType === PRICE_DISPLAY_TYPE.NO_DECIMALS_CEIL
      ? Math.ceil(price)
      : priceDisplayType === PRICE_DISPLAY_TYPE.NO_DECIMALS_ROUND
        ? Math.round(price)
        : price;

// The three selectors in precedence order; a brand with none of them floors.
type ResolvePriceDisplayTypeProps = {
  priceWithDecimals: boolean;
  priceNoDecimalsRoundUp: boolean;
  // Round to the nearest whole amount where the brand would floor (MPH-872). Unlike the two above it
  // is not a brand config but a resolved intent, so the caller names what is being rounded and why.
  priceNoDecimalsRound: boolean;
};
export const resolvePriceDisplayType = ({
  priceWithDecimals,
  priceNoDecimalsRoundUp,
  priceNoDecimalsRound,
}: ResolvePriceDisplayTypeProps): PRICE_DISPLAY_TYPE =>
  priceWithDecimals
    ? PRICE_DISPLAY_TYPE.DECIMALS
    : priceNoDecimalsRoundUp
      ? PRICE_DISPLAY_TYPE.NO_DECIMALS_CEIL
      : priceNoDecimalsRound
        ? PRICE_DISPLAY_TYPE.NO_DECIMALS_ROUND
        : PRICE_DISPLAY_TYPE.NO_DECIMALS_FLOOR;

// The pair of page-independent display types the config carries. Derived together here rather than
// at the call site so the results one cannot silently be given the detail one's rounding, or the
// other way round — the difference between them is only ever the detail-step rounding. (MPH-1101)
type PagePriceDisplayTypeConfig = Pick<ConfigValuesApp, 'priceDisplayType' | 'detailsPriceDisplayType'>;
type ResolveConfigPriceDisplayTypesProps = {
  priceWithDecimals: boolean;
  priceNoDecimalsRoundUp: boolean;
  // Whether the detail steps round where the brand would floor, after the MPH-900 kill switch.
  apexDetailsPriceRound: boolean;
};
export const resolveConfigPriceDisplayTypes = ({
  priceWithDecimals,
  priceNoDecimalsRoundUp,
  apexDetailsPriceRound,
}: ResolveConfigPriceDisplayTypesProps): PagePriceDisplayTypeConfig => ({
  priceDisplayType: resolvePriceDisplayType({ priceWithDecimals, priceNoDecimalsRoundUp, priceNoDecimalsRound: false }),
  detailsPriceDisplayType: resolvePriceDisplayType({
    priceWithDecimals,
    priceNoDecimalsRoundUp,
    priceNoDecimalsRound: apexDetailsPriceRound,
  }),
});

// The display type is the one page-dependent value in the config: the EVO detail steps round where
// the results steps floor (MPH-872). The config is resolved once per search and reused across in-app
// navigations without a refetch, so it carries both values page-independently and the current step's
// one is picked here, where the page type is known, instead of being frozen at the landing page —
// which floored the detail page after an in-app results→detail click while a hard load rounded it.
// (MPH-1101)
export const resolvePagePriceDisplayType = (
  pageType: PAGE_TYPE,
  { priceDisplayType, detailsPriceDisplayType }: PagePriceDisplayTypeConfig
): PRICE_DISPLAY_TYPE => (RESULTS_STEP_PAGE_TYPES.includes(pageType) ? priceDisplayType : detailsPriceDisplayType);

// MPH-900: price increments (branded-fare differences, transport sort increments) started following
// the display type in MPH-872, so the kill switch has to take them back to the pre-MPH-872 floor for
// every brand, not only the ones the APEX rounding ever applied to.
type ResolvePriceIncrementDisplayTypeProps = {
  priceDisplayType: PRICE_DISPLAY_TYPE;
  apexDetailsPriceRoundDisabled: boolean;
};
export const resolvePriceIncrementDisplayType = ({
  priceDisplayType,
  apexDetailsPriceRoundDisabled,
}: ResolvePriceIncrementDisplayTypeProps): PRICE_DISPLAY_TYPE =>
  apexDetailsPriceRoundDisabled ? PRICE_DISPLAY_TYPE.NO_DECIMALS_FLOOR : priceDisplayType;

type CreateFormatterProps = {
  locale: string;
  currency: string;
  currencyDigits: number;
  priceDisplayType: PRICE_DISPLAY_TYPE;
};
const createFormatter = ({ locale, currency, currencyDigits, priceDisplayType }: CreateFormatterProps) => {
  const minimumFractionDigits = priceDisplayType === PRICE_DISPLAY_TYPE.DECIMALS ? currencyDigits : 0;
  const options = { minimumFractionDigits };
  return new CurrencyFormatter(locale, currency, options);
};

type FormatPriceSpecificProps = {
  price: Price;
  locale: string;
  currency: string;
  currencyDigits?: number;
  priceDisplayType?: PRICE_DISPLAY_TYPE;
};
export const formatPriceSpecific = ({
  price,
  locale,
  currency,
  currencyDigits = DEFAULT_CURRENCY_DIGITS,
  priceDisplayType = PRICE_DISPLAY_TYPE.NO_DECIMALS_FLOOR,
}: FormatPriceSpecificProps) => {
  const priceNumber = typeof price === 'string' ? parseFloat(price) : price || 0;
  const priceNumberRounded = createPriceByDisplayType(priceNumber, priceDisplayType);
  return createFormatter({ locale, currency, currencyDigits, priceDisplayType }).formatNumber(priceNumberRounded);
};

type FormatPriceProps = {
  price: Price;
  localization: Localization;
  locale?: string; // Override the localization value
  currency?: string; // Override the localization value
  currencyDigits?: number;
};
export const formatPrice = ({
  price,
  localization,
  locale,
  currency,
  currencyDigits = DEFAULT_CURRENCY_DIGITS,
}: FormatPriceProps) =>
  formatPriceSpecific({
    price,
    locale: locale || localization.locale,
    currency: currency || localization.currency,
    currencyDigits,
    priceDisplayType: localization.configValuesApp.priceDisplayType,
  });
